import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnChanges,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormControl, Validators } from '@angular/forms';
import { catchError, filter, finalize, forkJoin, of, switchMap } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { BoardCompanyService } from '../../../../services/board-company.service';
import { BoardService } from '../../../../services/board.service';
import { DialogService } from '../../../../services/dialog.service';
import { NotificationService } from '../../../../services/notifications.service';
import { StackedSidebarService } from '../../../../services/stacked-sidebar.service';
import { SidebarType } from '../../../layout/stacked-sidebar/stacked-sidebar.interface';
import { FUNNEL_TYPE } from '../../../../enum/constants';
import {
  BoardCompany,
  BoardCompanyCard,
  BoardFunnel,
  UpdateCompanyPayload,
} from '../../board-v3.types';
import {
  customFieldEmailValidator,
  customFieldPhoneValidator,
  normalizePhonePayloadValue,
  sanitizePhoneInputValue,
} from '../../custom-field.validators';
import { AddressInput } from '../../../../interfaces/card';
import { TIMELINE_ENTITY_TYPE } from '../../../../interfaces/timeline.interface';
import { TimelineComponent } from '../../../timeline/timeline.component';
import { formatAddress, getAddressMapUrl, toAddressInput } from '../../address.utils';
import { formatContactPhone } from '../../contacts/contact.utils';
import { getCompanyEmails, getCompanyErrorKey, getCompanyName, getCompanyPhones } from '../company.utils';

type EditableCompanyField = 'name' | 'phones' | 'emails' | 'addresses';

export interface CompanyDetailResult {
  /** Latest state of the company when it was changed */
  company?: BoardCompany;
  deleted?: boolean;
}

@Component({
  selector: 'app-board-v3-company-detail',
  templateUrl: './company-detail.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
  host: { class: 'block h-full w-full min-h-0' },
})
export class BoardV3CompanyDetailComponent implements OnChanges {
  @Input() companyV2Id: string | null = null;
  @Input() canOpenCards = true;
  @Output() close = new EventEmitter<CompanyDetailResult | void>();
  @ViewChildren('editableControl') private editableControls!: QueryList<ElementRef<HTMLElement>>;
  @ViewChild(TimelineComponent) private timeline?: TimelineComponent;

  private readonly companyService = inject(BoardCompanyService);
  private readonly boardService = inject(BoardService);
  private readonly dialogService = inject(DialogService);
  private readonly notificationService = inject(NotificationService);
  private readonly stackedSidebarService = inject(StackedSidebarService);
  private readonly translateService = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  protected company: BoardCompany | null = null;
  protected loading = false;
  protected busy = false;
  protected saving = false;
  protected showCloseConfirmation = false;
  protected activeField: EditableCompanyField | null = null;
  protected readonly nameControl = new FormControl('', {
    nonNullable: true,
    validators: [Validators.required, Validators.maxLength(255)],
  });
  protected phonesArray = new FormArray<FormControl<string>>([]);
  protected emailsArray = new FormArray<FormControl<string>>([]);
  protected readonly addressesControl = new FormControl<AddressInput[]>([], { nonNullable: true });
  protected readonly getAddressMapUrl = getAddressMapUrl;
  protected readonly formatContactPhone = formatContactPhone;
  private changed = false;
  private funnels = new Map<string, BoardFunnel>();

  protected readonly TIMELINE_ENTITY_TYPE = TIMELINE_ENTITY_TYPE;

  get hasChanges(): boolean {
    return this.nameControl.dirty || this.phonesArray.dirty || this.emailsArray.dirty || this.addressesControl.dirty;
  }

  /** Name from the form, so the header follows the edits */
  get displayName(): string {
    return this.nameControl.value.trim() || getCompanyName(this.company);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['companyV2Id'] && this.companyV2Id) this.load(this.companyV2Id);
  }

  protected getFunnelName(card: BoardCompanyCard): string {
    return this.funnels.get(card.funnel_id)?.name ?? '';
  }

  protected getCardTypeKey(card: BoardCompanyCard): string {
    return this.funnels.get(card.funnel_id)?.type === FUNNEL_TYPE.LEAD
      ? 'board_v3_contact_card_lead'
      : 'board_v3_contact_card_deal';
  }

  protected formatAddress(address: AddressInput): string {
    return formatAddress(address, this.translate('address_apartment_short'));
  }

  protected phoneHref(phone: string): string {
    return `tel:${phone.replace(/[^\d+]/g, '')}`;
  }

  protected openCard(card: BoardCompanyCard): void {
    if (!this.canOpenCards) return;

    this.stackedSidebarService
      .openSidebar(
        { type: SidebarType.CardV3, width: '90vw' },
        { card_id: card.id, type: this.funnels.get(card.funnel_id)?.type ?? FUNNEL_TYPE.DEAL },
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.companyV2Id && this.load(this.companyV2Id, false));
  }

  // #region inline editing

  protected editField(field: EditableCompanyField): void {
    this.activeField = field;
    setTimeout(() => {
      const root = this.editableControls?.first?.nativeElement;
      const control = root instanceof HTMLInputElement ? root : root?.querySelector('input');
      control?.focus();
      control?.select();
    });
  }

  protected isFieldEditing(field: EditableCompanyField): boolean {
    return this.activeField === field;
  }

  @HostListener('document:click', ['$event'])
  protected closeActiveFieldOnOutsideClick(event: MouseEvent): void {
    if (!this.activeField) return;

    const target = event.target as Node | null;
    if (!target) return;

    const targetElement = target instanceof Element ? target : target.parentElement;
    if (targetElement?.closest('[data-company-edit-trigger="true"], .pac-container')) return;
    if (this.editableControls?.toArray().some((control) => control.nativeElement.contains(target))) return;

    this.activeField = null;
  }

  protected onTextFieldBlur(event: FocusEvent): void {
    const currentTarget = event.currentTarget as HTMLElement | null;
    const nextTarget = event.relatedTarget as Node | null;
    if (currentTarget && nextTarget && currentTarget.contains(nextTarget)) return;

    this.activeField = null;
  }

  protected get hasNameError(): boolean {
    return this.nameControl.invalid && this.nameControl.touched;
  }

  protected hasRowValues(field: 'phones' | 'emails'): boolean {
    return this.getFieldArray(field).controls.some((control) => control.value.trim());
  }

  protected getFieldArray(field: 'phones' | 'emails'): FormArray<FormControl<string>> {
    return field === 'phones' ? this.phonesArray : this.emailsArray;
  }

  protected addRow(field: 'phones' | 'emails'): void {
    const array = this.getFieldArray(field);
    array.push(this.createRowControl(field, ''));
    array.markAsDirty();
  }

  protected removeRow(field: 'phones' | 'emails', index: number): void {
    const array = this.getFieldArray(field);
    if (array.length <= 1) {
      array.at(0).setValue('');
    } else {
      array.removeAt(index);
    }
    array.markAsDirty();
  }

  protected onPhoneInput(control: FormControl<string>, event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = sanitizePhoneInputValue(input.value);
    if (value === input.value) return;

    input.value = value;
    control.setValue(value);
  }

  protected cancelChanges(): void {
    if (this.saving || !this.company) return;

    this.showCloseConfirmation = false;
    this.patchForm(this.company);
  }

  protected save(closeAfterSave = false): void {
    const company = this.company;
    if (this.saving || !company) return;

    this.nameControl.markAsTouched();
    this.phonesArray.markAllAsTouched();
    this.emailsArray.markAllAsTouched();
    // The editor's validator is detached once the field leaves edit mode, but the status is not recalculated
    this.addressesControl.updateValueAndValidity({ emitEvent: false });
    if (this.nameControl.invalid || this.phonesArray.invalid || this.emailsArray.invalid || this.addressesControl.invalid) {
      this.showCloseConfirmation = false;
      return;
    }

    const payload = this.buildPayload();
    if (!Object.keys(payload).length) {
      this.patchForm(company);
      if (closeAfterSave) this.emitClose();
      return;
    }

    this.saving = true;
    this.activeField = null;
    this.companyService
      .updateCompany(company.id, payload)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => (this.saving = false)),
      )
      .subscribe({
        next: (saved) => {
          this.company = saved;
          this.changed = true;
          this.patchForm(saved);
          this.loadTimeline();
          this.notificationService.showSuccess('', this.translate('board_v3_company_saved'));
          if (closeAfterSave) this.emitClose();
        },
        error: (error) => {
          this.showCloseConfirmation = false;
          this.notificationService.showError('', this.translate(getCompanyErrorKey(error)));
        },
      });
  }

  /** Called by the header button and by the sidebar backdrop / escape */
  requestClose(): void {
    if (this.saving || this.showCloseConfirmation) return;

    this.activeField = null;
    if (!this.hasChanges) {
      this.emitClose();
      return;
    }

    this.showCloseConfirmation = true;
  }

  protected cancelClose(): void {
    this.showCloseConfirmation = false;
  }

  protected discardChangesAndClose(): void {
    if (this.saving) return;

    this.emitClose();
  }

  private emitClose(): void {
    this.showCloseConfirmation = false;
    this.close.emit(this.changed && this.company ? { company: this.company } : undefined);
  }

  private patchForm(company: BoardCompany): void {
    this.activeField = null;
    this.nameControl.reset(company.name ?? '');
    this.phonesArray = this.buildRows('phones', getCompanyPhones(company));
    this.emailsArray = this.buildRows('emails', getCompanyEmails(company));
    this.addressesControl.reset((company.addresses ?? []).map(toAddressInput));
  }

  private buildRows(field: 'phones' | 'emails', values: string[]): FormArray<FormControl<string>> {
    return new FormArray((values.length ? values : ['']).map((value) => this.createRowControl(field, value)));
  }

  private createRowControl(field: 'phones' | 'emails', value: string): FormControl<string> {
    const validator = field === 'phones' ? customFieldPhoneValidator : customFieldEmailValidator;
    return new FormControl(value, { nonNullable: true, validators: [validator] });
  }

  /** Only the changed fields; `phones` / `emails` / `addresses` replace the whole list */
  private buildPayload(): UpdateCompanyPayload {
    const payload: UpdateCompanyPayload = {};

    if (this.nameControl.dirty) payload.name = this.nameControl.value.trim();
    if (this.phonesArray.dirty) {
      payload.phones = [
        ...new Set(
          this.phonesArray.controls.map((control) => normalizePhonePayloadValue(control.value)).filter(Boolean),
        ),
      ];
    }
    if (this.emailsArray.dirty) {
      payload.emails = [
        ...new Set(this.emailsArray.controls.map((control) => control.value.trim().toLowerCase()).filter(Boolean)),
      ];
    }
    if (this.addressesControl.dirty) payload.addresses = this.addressesControl.value;

    return payload;
  }

  // #endregion

  protected remove(): void {
    const company = this.company;
    if (!company || this.busy) return;

    this.dialogService
      .openConfirm({
        text: this.translateService.instant('board_v3_company_delete_confirm', { name: getCompanyName(company) }),
        isTranslate: true,
      })
      .pipe(
        filter(Boolean),
        switchMap(() => {
          this.busy = true;
          return this.companyService.deleteCompany(company.id).pipe(finalize(() => (this.busy = false)));
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.notificationService.showSuccess('', this.translate('board_v3_company_deleted'));
          this.close.emit({ deleted: true });
        },
        error: (error) => this.notificationService.showError('', this.translate(getCompanyErrorKey(error))),
      });
  }

  protected unlinkCard(card: BoardCompanyCard): void {
    const company = this.company;
    if (!company || this.busy) return;

    this.dialogService
      .openConfirm({
        text: this.translateService.instant('board_v3_company_unlink_card_confirm', { name: card.name }),
        isTranslate: true,
      })
      .pipe(
        filter(Boolean),
        switchMap(() => {
          this.busy = true;
          return this.companyService.unlinkCard(company.id, card.id).pipe(finalize(() => (this.busy = false)));
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (updated) => {
          this.company = updated;
          this.changed = true;
          this.loadTimeline();
        },
        error: (error) => this.notificationService.showError('', this.translate(getCompanyErrorKey(error))),
      });
  }

  /** After a change of the company its timeline is loaded again */
  protected loadTimeline(): void {
    this.timeline?.reload();
  }

  private load(companyV2Id: string, showLoader = true): void {
    this.loading = showLoader;
    forkJoin({
      company: this.companyService.getCompany(companyV2Id),
      leads: this.funnels.size
        ? of([])
        : this.boardService.getFunnels({ type: FUNNEL_TYPE.LEAD }).pipe(catchError(() => of([]))),
      deals: this.funnels.size
        ? of([])
        : this.boardService.getFunnels({ type: FUNNEL_TYPE.DEAL }).pipe(catchError(() => of([]))),
    })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => (this.loading = false)),
      )
      .subscribe({
        next: ({ company, leads, deals }) => {
          [...leads, ...deals].forEach((funnel) => this.funnels.set(funnel.id, funnel));
          this.company = company;
          if (!this.hasChanges) this.patchForm(company);
          this.loadTimeline();
        },
        error: (error) => {
          this.notificationService.showError('', this.translate(getCompanyErrorKey(error)));
          if (!this.company) this.close.emit();
        },
      });
  }

  private translate(key: string): string {
    return String(this.translateService.instant(key));
  }
}
